import React, { useState } from 'react';
import { Trash2, Edit2, X, Check } from 'lucide-react';
import { Market, MarketSearch } from '../../types/market';
import { useMarkets, MarketError } from '../../hooks/useMarkets';

interface MarketSearchesProps {
  market: Market;
  searches: MarketSearch[];
  onDeleteSearch: (marketSearchId: string, searchId: string) => Promise<void>;
}

export const MarketSearches: React.FC<MarketSearchesProps> = ({
  market,
  searches,
  onDeleteSearch
}) => {
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [error, setError] = useState('');
  const { updateSearch } = useMarkets();

  const handleDeleteSearch = async (marketSearchId: string, searchId: string) => {
    await onDeleteSearch(marketSearchId, searchId);
  };

  const startEdit = (search: MarketSearch) => {
    setEditingId(search.id);
    setEditValue(search.search.query);
    setError('');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditValue('');
    setError('');
  };

  const handleUpdate = async (searchId: string) => {
    try {
      setError('');
      await updateSearch({ marketSearchId: searchId, query: editValue });
      setEditingId(null);
      setEditValue('');
    } catch (err) {
      if (err instanceof MarketError) {
        setError(err.message);
      } else if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('An unexpected error occurred while updating the search');
      }
    }
  };

  return (
    <div className="mt-4 pl-4 border-l-2 border-gray-100 dark:border-gray-700">
      <h4 className="text-sm font-medium text-gray-500 dark:text-gray-400 mb-2">Searches</h4>
      {searches.length === 0 ? (
        <p className="text-sm text-gray-400 dark:text-gray-500 italic">No searches added yet</p>
      ) : (
        <div className="space-y-2">
          {searches.map((item) => (
            <div
              key={item.id}
              className="flex items-start justify-between p-2 rounded-lg bg-gray-50 dark:bg-gray-700/50 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors group"
            >
              <div className="flex-1 mr-4">
                {editingId === item.id ? (
                  <div className="space-y-2">
                    <textarea
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      className={`w-full p-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${
                        error ? 'border-red-300 dark:border-red-500 bg-red-50 dark:bg-red-900/20' : 'border-gray-300 dark:border-gray-600'
                      }`}
                      rows={2}
                    />
                    {error && (
                      <div className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-2 rounded-lg flex items-start space-x-2">
                        <span className="flex-shrink-0">!</span>
                        <span>{error}</span>
                      </div>
                    )}
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handleUpdate(item.id)}
                        className="p-1.5 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors"
                        title="Save changes"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button
                        onClick={cancelEdit}
                        className="p-1.5 text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                        title="Cancel"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className="text-sm text-gray-700 dark:text-gray-200">{item.search.query}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {item.search.searches.toLocaleString()} searches
                      </span>
                      <span className="text-xs text-gray-300 dark:text-gray-600">•</span>
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {item.search.likes} likes
                      </span>
                    </div>
                  </>
                )}
              </div>
              {editingId !== item.id && (
                <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => startEdit(item)}
                    className="p-1.5 text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                    title="Edit search"
                  >
                    <Edit2 className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteSearch(item.id, item.search.id)}
                    className="p-1.5 text-gray-400 hover:text-red-600 dark:hover:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    title="Delete search"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};